import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Search, X, Pencil, Trash2 } from 'lucide-react';
import api from '../../services/api';
import { gstinError } from '../../utils/gstin';
import alertService from '../../utils/alert';
import { cn } from '../../utils/cn';
import { formatCurrency } from '../../utils/currency';
import { useCompanyCurrency } from '../../hooks/useCompanySettings';
import { CountrySelect, StateSelect } from '../../components/CountryStateSelect';
import PageHeader from '../../components/PageHeader';
import { PAGE } from '../../components/crm/ui';

const INPUT = 'w-full h-9 px-3 bg-white border border-[#D8DDE6] rounded text-[13px] outline-none focus:border-[#0176D3]';
const LABEL = 'block text-[11px] font-semibold text-[#706E6B] mb-1';
const errMsg = (e, fallback) => e?.response?.data?.message || e?.response?.data?.error || fallback;
const BLANK = {
  name: '', contactPerson: '', email: '', phone: '', gstin: '', pan: '', taxId: '', address: '', city: '', state: '', pinCode: '',
  country: 'India', defaultCurrency: 'INR', paymentTermsDays: 30, defaultExpenseAccountId: '', notes: '', status: 'active', tdsSection: '', tdsRate: 0,
  bankDetails: { accountName: '', accountNumber: '', bankName: '', ifscCode: '', branch: '' },
};

function VendorForm({ vendor, accounts, onClose, onSaved }) {
  const [form, setForm] = useState(() => (vendor
    ? { ...BLANK, ...vendor, defaultExpenseAccountId: vendor.defaultExpenseAccountId || '', bankDetails: { ...BLANK.bankDetails, ...(vendor.bankDetails || {}) } }
    : BLANK));
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setBank = (patch) => setForm((f) => ({ ...f, bankDetails: { ...f.bankDetails, ...patch } }));
  const isIndia = form.country === 'India';

  const save = async () => {
    if (!form.name.trim()) return alertService.error('Vendor name is required');
    if (gstinError(form.gstin)) return alertService.error('GSTIN looks wrong', gstinError(form.gstin));
    setSaving(true);
    const payload = { ...form, defaultExpenseAccountId: form.defaultExpenseAccountId || null, paymentTermsDays: Number(form.paymentTermsDays) || 0 };
    ['_id', 'companyId', 'createdAt', 'updatedAt', '__v', 'outstanding', 'openBills', 'createdBy'].forEach((k) => delete payload[k]);
    try {
      if (vendor) await api.put(`/crm/purchases/vendors/${vendor._id}`, payload);
      else await api.post('/crm/purchases/vendors', payload);
      onSaved();
    } catch (e) {
      alertService.error('Could not save vendor', errMsg(e, 'Please check the details'));
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, props = {}) => (
    <div className={props.span}>
      <label className={LABEL}>{label}</label>
      <input aria-label={label.replace(' *', '')} className={INPUT} value={form[key] ?? ''} onChange={(e) => set({ [key]: e.target.value })} {...props.input} />
    </div>
  );

  return (
    <div className="fixed inset-0 z-[100] bg-black/40 flex items-end sm:items-start justify-center overflow-y-auto sm:p-4" onMouseDown={onClose}>
      <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-3xl sm:my-8" onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-[#E5E5E5]">
          <h2 className="text-[16px] font-bold text-[#2E2E2E]">{vendor ? `Edit ${vendor.name}` : 'New vendor'}</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="p-1 rounded hover:bg-[#F3F2F2]"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-5">
          <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {field('name', 'Vendor name *', { span: 'md:col-span-2' })}
            {field('contactPerson', 'Contact person')}
            {field('email', 'Email', { input: { type: 'email' } })}
            {field('phone', 'Phone')}
            <div>
              <label className={LABEL}>Status</label>
              <select aria-label="Status" className={INPUT} value={form.status} onChange={(e) => set({ status: e.target.value })}><option value="active">Active</option><option value="inactive">Inactive</option></select>
            </div>
          </section>

          <section>
            <h3 className="text-[12px] font-bold text-[#706E6B] uppercase tracking-wide mb-2">Address & tax</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><label className={LABEL}>Country</label><CountrySelect value={form.country} onChange={(v) => set({ country: v, state: '' })} /></div>
              <div><label className={LABEL}>State / region</label><StateSelect country={form.country} value={form.state} onChange={(v) => set({ state: v })} /></div>
              {field('city', 'City')}
              {field('address', 'Address', { span: 'md:col-span-2' })}
              {field('pinCode', isIndia ? 'PIN code' : 'Postal code')}
              {isIndia ? (
                <>
                  {field('gstin', 'GSTIN', { input: { placeholder: '15 characters, optional' } })}
                  {field('pan', 'PAN')}
                </>
              ) : field('taxId', 'Tax ID (VAT / TRN / EIN)')}
            </div>
            {isIndia && <p className="text-[11px] text-[#706E6B] mt-2">The vendor's state decides CGST + SGST (same state as you) or IGST (other state) on their bills.</p>}
          </section>

          <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div><label className={LABEL}>Currency</label><input aria-label="Currency" className={INPUT} value={form.defaultCurrency} maxLength={3} onChange={(e) => set({ defaultCurrency: e.target.value.toUpperCase() })} /></div>
            <div><label className={LABEL}>Payment terms (days)</label><input aria-label="Payment terms (days)" type="number" min="0" max="365" className={INPUT} value={form.paymentTermsDays} onChange={(e) => set({ paymentTermsDays: e.target.value })} /></div>
            <div>
              <label className={LABEL}>Default expense account</label>
              <select aria-label="Default expense account" className={INPUT} value={form.defaultExpenseAccountId} onChange={(e) => set({ defaultExpenseAccountId: e.target.value })}>
                <option value="">Operating Expenses (default)</option>
                {accounts.map((a) => <option key={a._id} value={a._id}>{a.code} · {a.name}</option>)}
              </select>
            </div>
          </section>

          {isIndia && (
            <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><label className={LABEL} htmlFor="v-tdssec">TDS section</label><input id="v-tdssec" className={INPUT} placeholder="e.g. 194C, 194J" value={form.tdsSection} onChange={(e) => set({ tdsSection: e.target.value.toUpperCase() })} /></div>
              <div><label className={LABEL} htmlFor="v-tdsrate">TDS rate %</label><input id="v-tdsrate" type="number" min="0" max="100" step="0.01" className={INPUT} value={form.tdsRate} onChange={(e) => set({ tdsRate: Number(e.target.value) || 0 })} /></div>
              <p className="text-[11px] text-[#706E6B] self-end">Pre-fills TDS when you pay this vendor. Leave the rate at 0 if no TDS applies.</p>
            </section>
          )}

          <section>
            <h3 className="text-[12px] font-bold text-[#706E6B] uppercase tracking-wide mb-2">Bank details (for paying them)</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {[['accountName', 'Account name'], ['accountNumber', 'Account number'], ['bankName', 'Bank'], ['ifscCode', isIndia ? 'IFSC' : 'SWIFT / routing'], ['branch', 'Branch']].map(([k, l]) => (
                <div key={k}><label className={LABEL}>{l}</label><input aria-label={l} className={INPUT} value={form.bankDetails[k]} onChange={(e) => setBank({ [k]: e.target.value })} /></div>
              ))}
            </div>
          </section>

          <div><label className={LABEL}>Notes</label><textarea aria-label="Notes" rows={2} className={cn(INPUT, 'h-auto py-2')} value={form.notes} onChange={(e) => set({ notes: e.target.value })} /></div>
        </div>
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-[#D8DDE6] bg-[#FAFAF9] rounded-b-lg">
          <button type="button" onClick={onClose} className="h-9 px-4 rounded border border-[#C9C9C9] text-[13px] font-semibold bg-white">Cancel</button>
          <button type="button" disabled={saving} onClick={save} className="hover:bg-[#014486] h-9 px-4 rounded bg-[#0176D3] text-white text-[13px] font-semibold disabled:opacity-60">{saving ? 'Saving…' : 'Save vendor'}</button>
        </div>
      </div>
    </div>
  );
}

export default function VendorsPage() {
  const currency = useCompanyCurrency();
  const [vendors, setVendors] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('active');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | 'new' | vendor

  const load = useCallback(() => {
    setLoading(true);
    api.get('/crm/purchases/vendors', { params: { search: search || undefined, status: status || undefined } })
      .then((res) => setVendors(res.data.vendors || []))
      .catch((e) => alertService.error('Could not load vendors', errMsg(e, '')))
      .finally(() => setLoading(false));
  }, [search, status]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get('/crm/purchases/expense-accounts').then((res) => setAccounts(res.data.accounts || [])).catch(() => {}); }, []);

  const remove = async (v) => {
    if (!(await alertService.confirm(`Delete ${v.name}?`, 'Vendors with bills cannot be deleted — mark them inactive instead.', 'danger', 'Delete'))) return;
    try { await api.delete(`/crm/purchases/vendors/${v._id}`); alertService.success('Vendor deleted'); load(); } catch (e) { alertService.error('Could not delete', errMsg(e, '')); }
  };

  const totalOutstanding = vendors.reduce((s, v) => s + (v.outstanding || 0), 0);

  return (
    <div className={PAGE}>
      <PageHeader title="Vendors" subtitle={<>Suppliers you buy from. You owe <strong className="text-[#2E2E2E] tabular-nums">{formatCurrency(totalOutstanding, currency)}</strong> across the vendors shown.</>} actions={<>
        <button type="button" onClick={() => setEditing('new')} className="h-9 px-4 rounded bg-[#0176D3] text-white text-[13px] font-semibold flex items-center gap-1.5"><Plus size={15} /> New Vendor</button>
      </>} />

      <div className="bg-white border border-[#E5E5E5] rounded-lg shadow-[0_1px_3px_rgba(0,0,0,0.08)]">
        <div className="flex flex-wrap items-center gap-2 p-3 border-b border-[#D8DDE6]">
          {[['active', 'Active'], ['inactive', 'Inactive'], ['', 'All']].map(([k, l]) => (
            <button type="button" key={l} onClick={() => setStatus(k)} className={cn('h-8 px-3 rounded text-[12px] font-semibold border', status === k ? 'bg-[#0176D3] text-white border-[#0176D3]' : 'bg-white text-[#444444] border-[#D8DDE6]')}>{l}</button>
          ))}
          <div className="relative ml-auto w-full sm:w-72">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#706E6B]" />
            <input className={cn(INPUT, 'pl-8')} placeholder="Search name, GSTIN, email, phone…" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[13px] min-w-[47.5rem]">
            <thead className="text-[#706E6B] text-[11px] uppercase tracking-wide">
              <tr className="border-b border-[#D8DDE6]">
                <th className="text-left font-semibold px-4 py-2.5">Vendor</th><th className="text-left font-semibold px-4 py-2.5">GSTIN / Tax ID</th>
                <th className="text-left font-semibold px-4 py-2.5">Location</th><th className="text-right font-semibold px-4 py-2.5">Open bills</th>
                <th className="text-right font-semibold px-4 py-2.5">Outstanding</th><th className="px-4 py-2.5" />
              </tr>
            </thead>
            <tbody>
              {vendors.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center text-[#706E6B]">{loading ? 'Loading…' : 'No vendors yet. Add the suppliers you buy from.'}</td></tr>}
              {vendors.map((v) => (
                <tr key={v._id} className="border-b border-[#F3F2F2] hover:bg-[#F7F9FB]">
                  <td className="px-4 py-2.5">
                    <span className="font-semibold text-[#2E2E2E]">{v.name}</span>
                    {v.status === 'inactive' && <span className="ml-2 text-[10px] font-semibold bg-[#F3F2F2] text-[#706E6B] px-1.5 py-0.5 rounded">Inactive</span>}
                    <span className="block text-[11px] text-[#706E6B]">{[v.contactPerson, v.email, v.phone].filter(Boolean).join(' · ')}</span>
                  </td>
                  <td className="px-4 py-2.5 font-mono text-[12px]">{v.gstin || v.taxId || '—'}</td>
                  <td className="px-4 py-2.5">{[v.city, v.state, v.country !== 'India' ? v.country : ''].filter(Boolean).join(', ') || '—'}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{v.openBills}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums font-semibold">{formatCurrency(v.outstanding, v.defaultCurrency || currency)}</td>
                  <td className="px-4 py-2.5 text-right whitespace-nowrap">
                    <button type="button" onClick={() => setEditing(v)} aria-label={`Edit ${v.name}`} className="p-1.5 rounded hover:bg-[#F3F2F2] text-[#706E6B]"><Pencil size={14} /></button>
                    <button type="button" onClick={() => remove(v)} aria-label={`Delete ${v.name}`} className="p-1.5 rounded hover:bg-[#F3F2F2] text-[#706E6B] hover:text-[#BA0517]"><Trash2 size={14} /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <VendorForm
          vendor={editing === 'new' ? null : editing}
          accounts={accounts}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); alertService.success('Vendor saved'); load(); }}
        />
      )}
    </div>
  );
}
